import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getConfig } from '../services/assistantService';

/**
 * Is "TimeGlobe Intelligence" switched on for the signed-in account?
 *
 * The answer comes from `GET /assistant/config` and is fetched once per page
 * load: the first caller starts the request, every other caller (sidebar, the
 * mode switch, "Problem melden", the page itself) shares it, and later page
 * changes read the settled answer without a flicker. Any error counts as
 * "off". The answer belongs to an account, so it is keyed by it — a login as
 * someone else never sees the previous account's answer.
 *
 * `ready` is false until the answer is known: callers that must not flash the
 * wrong thing (the page's redirect, a link target) wait for it.
 */
export interface AssistantEnabledState {
  enabled: boolean;
  ready: boolean;
}

interface Cached {
  accountKey: string;
  promise: Promise<boolean>;
  /** Set once the request has settled. */
  settled?: boolean;
}

let cached: Cached | null = null;

function request(accountKey: string): Cached {
  if (cached?.accountKey === accountKey) return cached;
  const entry: Cached = {
    accountKey,
    promise: getConfig().then(
      (config) => config.enabled,
      () => false,
    ),
  };
  void entry.promise.then((enabled) => {
    entry.settled = enabled;
  });
  cached = entry;
  return entry;
}

/** The answer when it is already known, without asking. */
function settledAnswer(accountKey: string): boolean | null {
  return cached?.accountKey === accountKey && cached.settled !== undefined ? cached.settled : null;
}

/** For tests: forget the page's answer. */
export function resetAssistantEnabledCache(): void {
  cached = null;
}

export function useAssistantEnabled(): AssistantEnabledState {
  const { currentUser, isAuthenticated } = useAuth();
  const accountKey = isAuthenticated ? currentUser?.id || currentUser?.email || 'account' : '';
  const [answer, setAnswer] = useState<{ accountKey: string; enabled: boolean } | null>(() => {
    const known = accountKey ? settledAnswer(accountKey) : null;
    return known === null ? null : { accountKey, enabled: known };
  });

  useEffect(() => {
    if (!accountKey) return;
    let active = true;
    void request(accountKey).promise.then((enabled) => {
      if (active) setAnswer({ accountKey, enabled });
    });
    return () => {
      active = false;
    };
  }, [accountKey]);

  if (!accountKey || answer?.accountKey !== accountKey) return { enabled: false, ready: false };
  return { enabled: answer.enabled, ready: true };
}
